import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";
import { Popover } from "antd";
import TableLoader from "./TableLoader";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Initialize pdfMake with fonts
if (typeof pdfMake !== 'undefined') {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
}

// Format number with commas
const formatAmount = (amount) => {
  if (amount >= 1000) {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return amount;
};

const Table = ({ loading, tableData, subCategory, showSearchForm, setShowSearchForm }) => {
  const [sortedData, setSortedData] = useState(null);
  const [sortedDirection, setSortedDirection] = useState("desc"); // Default to descending
  const [sortColumn, setSortColumn] = useState("amount"); // Default sort by amount
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPlacement, setTooltipPlacement] = useState("right");
  const rowRefs = useRef({});
  const tableRef = useRef(null);

  // Check and update tooltip placement when needed
  useEffect(() => {
    const checkTooltipPlacement = () => {
      if (hoveredRow !== null && rowRefs.current[hoveredRow] && tableRef.current) {
        const tableRight = tableRef.current.getBoundingClientRect().right;
        const availableWidth = window.innerWidth - tableRight;
        
        // If less than 250px available on right, show tooltip at top
        // You may need to adjust this value based on your tooltip's width
        if (availableWidth < 250) {
          setTooltipPlacement("topRight");
        } else {
          setTooltipPlacement("right");
        }
      }
    };

    checkTooltipPlacement();
    
    // Check placement on resize and scroll
    window.addEventListener("resize", checkTooltipPlacement);
    window.addEventListener("scroll", checkTooltipPlacement);
    
    return () => {
      window.removeEventListener("resize", checkTooltipPlacement);
      window.removeEventListener("scroll", checkTooltipPlacement);
    };
  }, [hoveredRow]);

  // Process table data on initial load and set default sorting
  useEffect(() => {
    if (tableData && Array.isArray(tableData)) {
      const data = [...tableData];
      // Default sort by amount_sum in descending order
      data.sort((a, b) => b.amount_sum - a.amount_sum);
      setSortedData(data);
      setSortColumn("amount");
      setSortedDirection("desc");
    }
  }, [tableData]);

  // Calculate dense rank - modified to implement proper dense ranking
  const calculateRank = (index, data) => {
    if (!data || data.length === 0) return 1;
    
    const sortField = sortColumn === "deals" ? "deal_count" : "amount_sum";
    
    // Calculate dense ranking by comparing with the previous item
    if (index === 0) return 1;
    
    const currentValue = data[index][sortField];
    const previousValue = data[index - 1][sortField];
    const previousRank = calculateRank(index - 1, data);
    
    return currentValue === previousValue ? previousRank : previousRank + 1;
  };

  const sortByDealsHandler = () => {
    if (!sortedData) return;
    let sorted = [...sortedData];

    // Otherwise sort by deals desc
    sorted.sort((a, b) => b.deal_count - a.deal_count);
    
    setSortColumn("deals");
    setSortedData(sorted);
  };

  const sortByAmountHandler = () => {
    if (!sortedData) return;
    let sorted = [...sortedData];
    
    sorted.sort((a, b) => b.amount_sum - a.amount_sum);
    
    setSortColumn("amount");
    setSortedData(sorted);
  };

  const handleRowMouseEnter = (index) => {
    setHoveredRow(index);
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(null);
  };

  // Custom tooltip content component
  const NotableDealTooltip = ({ content }) => (
    <div className="w-48 ">
      <h3 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-1 border-gray-200">
        📌 Notable Deals
      </h3>
      <p className="text-xs">{content}</p>
    </div>
  );

  const renderTableData = sortedData || [];

  // Generate sort title text
  const getSortTitle = () => {
    const sub = subCategory === "pe" ? "PE" : "M&A";
    return `Transactions in ${sub} Sorted by ${sortColumn === "deals" ? "Deals" : "Amount"} (Descending)`;
  };

  return (
    <div className="relative">
      {loading ? (
        <TableLoader />
      ) : (
        <div className="text-center overflow-x-hidden md:flex flex-col items-center">
          {/* Added sort title */}
          <div className="text-sm text-gray-600 font-medium mb-2">
            {getSortTitle()}
          </div>
          <table 
            ref={tableRef}
            className="md:mx-4 bg-[aliceblue] text-xs font-sans rounded-lg overflow-hidden md:text-xl md:leading-[3rem] lg:w-full"
          >
            <thead className="border-b border-[#A26F35]">
              <tr className="text-[#A26F35]">
                <th className="py-2 px-4">Rank</th>
                <th className="text-left">Company Name</th>
                <th className="px-8 cursor-pointer" onClick={sortByDealsHandler}>
                  #of Deals <FontAwesomeIcon icon={faSort} size="xs" style={{ color: "#a26f35" }} />
                </th>
                <th className="px-8 py-[0.6rem] cursor-pointer" onClick={sortByAmountHandler}>
                  Amount $M <FontAwesomeIcon icon={faSort} size="xs" style={{ color: "#a26f35" }} />
                </th>
              </tr>
            </thead>
            <tbody>
              {renderTableData.length > 0 ? (
                renderTableData.map((company, index) => (
                  <Popover 
                    key={index}
                    content={company.notable_deal ? <NotableDealTooltip content={company.notable_deal} /> : null}
                    placement={tooltipPlacement}
                    overlayClassName="notable-deal-tooltip"
                    visible={hoveredRow === index && company.notable_deal ? true : false}
                    overlayStyle={{ 
                      zIndex: 1050,
                      padding: 0,
                      background: 'white',
                      color: 'black',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8'
                    }}
                    align={{
                      offset: [20, 0], // Adjust horizontal offset as needed
                    }}
                  >
                    <tr 
                      className="border-t-2 hover:bg-gray-100"
                      ref={el => rowRefs.current[index] = el}
                      onMouseEnter={() => handleRowMouseEnter(index)}
                      onMouseLeave={handleRowMouseLeave}
                    >
                      <td>{calculateRank(index, renderTableData)}</td>
                      <td className="text-left">{company.advisor_name}</td>
                      <td>{company.deal_count}</td>
                      <td>{formatAmount(company.amount_sum)}</td>
                    </tr>
                  </Popover>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="py-4 text-center text-gray-500">No Data Found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Table;